import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import type { TimeRange } from "@shared/types";
export type { TimeRange };
export const rangeOptions: { value: TimeRange; label: string }[] = [
  { value: "1h", label: "1 小时" }, { value: "6h", label: "6 小时" }, { value: "24h", label: "24 小时" }, { value: "7d", label: "7 天" }
];

/** Window length (ms) for each preset — used to derive a custom from/to seed. */
export const RANGE_MS: Record<TimeRange, number> = {
  "1h": 3_600_000, "6h": 21_600_000, "24h": 86_400_000, "7d": 604_800_000
};

/** An absolute, time-navigable window the charts query + draw their axis over. */
export interface AbsWindow { from: number; to: number; }

/** The query params a metrics request carries (preset OR absolute window). */
export interface MetricsQuery { range: TimeRange; from?: number; to?: number; }

interface ConsoleState {
  range: TimeRange;
  /** Choosing a preset clears any custom absolute window. */
  setRange: (r: TimeRange) => void;
  /** The active absolute window, or null when a preset is in effect. */
  win: AbsWindow | null;
  /** Set an absolute from/to window (from the picker, a brush-zoom, or a pan). */
  setWin: (w: AbsWindow | null) => void;
  /** The effective from/to the charts should draw their axis over right now. */
  effectiveWindow: AbsWindow;
  /** Query params for the metrics API (absolute window wins over the preset). */
  query: MetricsQuery;
  live: boolean;
  setLive: (b: boolean) => void;
  tick: number;
}
const Ctx = createContext<ConsoleState | null>(null);

export function ConsoleProvider({ children }: { children: ReactNode }): JSX.Element {
  const [range, setRangeRaw] = useState<TimeRange>("24h");
  const [win, setWin] = useState<AbsWindow | null>(null);
  const [live, setLive] = useState(false);
  const [tick, setTick] = useState(0);
  const timer = useRef<number | null>(null);

  // A preset and a custom window are mutually exclusive: picking a preset drops
  // the custom window so the topbar never shows both as active.
  const setRange = (r: TimeRange): void => { setRangeRaw(r); setWin(null); };

  useEffect(() => {
    if (live) {
      timer.current = window.setInterval(() => setTick((t) => t + 1), 10_000);
      return () => { if (timer.current !== null) window.clearInterval(timer.current); };
    }
    return undefined;
  }, [live]);

  // When no custom window is set, the effective window is the preset ending now.
  // It is recomputed per tick so a live preset chart keeps scrolling forward.
  const effectiveWindow = useMemo<AbsWindow>(() => {
    if (win !== null) return win;
    const to = Date.now();
    return { from: to - RANGE_MS[range], to };
    // tick intentionally in deps so a live preset advances its axis.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, range, tick]);

  const query = useMemo<MetricsQuery>(
    () => (win !== null ? { range, from: win.from, to: win.to } : { range }),
    [win, range]
  );

  const value = useMemo(
    () => ({ range, setRange, win, setWin, effectiveWindow, query, live, setLive, tick }),
    [range, win, effectiveWindow, query, live, tick]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useConsole(): ConsoleState {
  const v = useContext(Ctx);
  if (v === null) throw new Error("useConsole must be used inside ConsoleProvider");
  return v;
}
